/**
 * A custom element that shows/hides a fixed "add to cart" bar once the shopper scrolls
 * past the real add to cart button (`data-target`), and mirrors that button's label/price
 * and disabled state (kept in sync when the shopper changes variant).
 */
class StickyAddToCart extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /** @type {MutationObserver | undefined} */
  #syncObserver;

  #scrolledPast = false;

  #footerInView = false;

  connectedCallback() {
    const targetSelector = this.dataset.target;
    const target = targetSelector ? document.querySelector(targetSelector) : null;

    if (!target) {
      this.hidden = true;
      return;
    }

    this.inert = true;

    // Hide the bar again once the footer is on screen, so it doesn't cover the footer content.
    const footer = document.querySelector('.shopify-section-group-footer-group');

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === footer) {
            // Stay hidden once the footer top has been reached, even when scrolled past it
            // (the first footer-group section can be empty, so it leaves the viewport at the bottom).
            this.#footerInView = entry.isIntersecting || entry.boundingClientRect.top < 0;
          } else {
            // Only reveal the bar once the real button has scrolled above the viewport,
            // not before the shopper has reached it.
            this.#scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          }
        }

        const visible = this.#scrolledPast && !this.#footerInView;
        this.classList.toggle('sticky-add-to-cart--visible', visible);
        // Keep the off-screen bar out of the tab order/accessibility tree until it's shown.
        this.inert = !visible;
      },
      { threshold: 0 }
    );
    this.#visibilityObserver.observe(target);
    if (footer) this.#visibilityObserver.observe(footer);

    this.#syncWithSource(target);
  }

  disconnectedCallback() {
    this.#visibilityObserver?.disconnect();
    this.#syncObserver?.disconnect();
  }

  /**
   * Keeps the sticky button's price/label and disabled state mirrored to the real
   * add to cart button, so a variant change (handled elsewhere via section morphing)
   * is reflected here too.
   * @param {Element} target
   */
  #syncWithSource(target) {
    const sourceButton = target.querySelector('button[type="submit"][name="add"]');
    const sourceText = sourceButton?.querySelector('.add-to-cart-text__content');
    const ownButton = this.querySelector('button[type="submit"][name="add"]');
    const ownText = ownButton?.querySelector('.add-to-cart-text__content');

    if (!(sourceButton instanceof HTMLButtonElement) || !sourceText || !ownText) return;
    if (!(ownButton instanceof HTMLButtonElement)) return;

    this.#syncObserver = new MutationObserver(() => {
      ownText.innerHTML = sourceText.innerHTML;
      ownButton.disabled = sourceButton.disabled;
    });

    this.#syncObserver.observe(sourceButton, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCart);
}
